'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './AuthContext';

interface CartItem {
  id: string | number;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  weight?: string;
  cookingInstructions?: string;
  subscriptionOption?: string;
  foodPreference?: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: any) => void;
  removeFromCart: (productId: string | number) => void;
  updateQuantity: (productId: string | number, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('organic_heaven_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart', e);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save cart to local storage
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('organic_heaven_cart', JSON.stringify(cart));
    }
  }, [cart, isLoaded]);

  // Handle restoring pending items on login success
  useEffect(() => {
    if (isAuthenticated) {
      const pendingItemStr = localStorage.getItem('organic_heaven_pending_cart_item');
      if (pendingItemStr) {
        try {
          const pendingItem = JSON.parse(pendingItemStr);
          setCart(prevCart => {
            const existingItem = prevCart.find(item => 
              item.id === pendingItem.id && 
              item.weight === pendingItem.weight && 
              item.cookingInstructions === pendingItem.cookingInstructions
            );
            if (existingItem) {
              return prevCart.map(item =>
                (item.id === pendingItem.id && 
                 item.weight === pendingItem.weight && 
                 item.cookingInstructions === pendingItem.cookingInstructions)
                  ? { ...item, quantity: item.quantity + (pendingItem.quantity || 1) }
                  : item
              );
            }
            return [...prevCart, { 
              id: pendingItem.id || `${pendingItem.name}-${pendingItem.weight || 'default'}`, 
              name: pendingItem.name, 
              price: pendingItem.price, 
              quantity: pendingItem.quantity || 1, 
              image: pendingItem.image,
              weight: pendingItem.weight,
              cookingInstructions: pendingItem.cookingInstructions,
              subscriptionOption: pendingItem.subscriptionOption,
              foodPreference: pendingItem.foodPreference
            }];
          });
        } catch (e) {
          console.error('Failed to restore pending cart item', e);
        } finally {
          localStorage.removeItem('organic_heaven_pending_cart_item');
        }
      }
    }
  }, [isAuthenticated]);

  const addToCart = (product: any) => {
    if (!isAuthenticated) {
      // Save pending product to localStorage so it can be restored post-login
      localStorage.setItem('organic_heaven_pending_cart_item', JSON.stringify(product));
      alert("Please log in to add products to your cart.");
      router.push('/login');
      return;
    }
    setCart(prevCart => {
      const existingItem = prevCart.find(item => 
        item.id === product.id && 
        item.weight === product.weight && 
        item.cookingInstructions === product.cookingInstructions
      );
      if (existingItem) {
        return prevCart.map(item =>
          (item.id === product.id && 
           item.weight === product.weight && 
           item.cookingInstructions === product.cookingInstructions)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { 
        id: product.id || `${product.name}-${product.weight || 'default'}`, 
        name: product.name, 
        price: product.price, 
        quantity: 1, 
        image: product.image,
        weight: product.weight,
        cookingInstructions: product.cookingInstructions,
        subscriptionOption: product.subscriptionOption,
        foodPreference: product.foodPreference
      }];
    });
  };

  const removeFromCart = (productId: string | number) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: string | number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => prevCart.map(item => item.id === productId ? { ...item, quantity } : item));
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
